import { ProductLookupResult as LookupResult } from '@/services/openFoodFactsService';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';

interface ProductLookupResultProps {
  result: LookupResult | null;
  isLoading: boolean;
  onUseProduct: (product: NonNullable<LookupResult['product']>) => void;
  onManualEntry: () => void;
  onScanAgain: () => void;
}

export function ProductLookupResultCard({
  result,
  isLoading,
  onUseProduct,
  onManualEntry,
  onScanAgain,
}: ProductLookupResultProps) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 text-center">
        <Spinner size="lg" className="mx-auto mb-4" />
        <p className="text-gray-600">Recherche du produit...</p>
        <p className="text-sm text-gray-400 mt-1">Interrogation de la base Open Food Facts</p>
      </div>
    );
  }

  if (!result) {
    return null;
  }

  if (!result.found || !result.product) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="text-center mb-4">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 20h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Produit non trouvé</h3>
          <p className="text-gray-500 text-sm mt-1">
            Code-barres: <span className="font-mono">{result.barcode}</span>
          </p>
          {result.error && (
            <p className="text-orange-600 text-sm mt-2">{result.error}</p>
          )}
        </div>

        <div className="flex gap-2 justify-center">
          <Button onClick={onManualEntry}>
            Créer manuellement
          </Button>
          <Button variant="outline" onClick={onScanAgain}>
            Scanner à nouveau
          </Button>
        </div>
      </div>
    );
  }

  const { product } = result;

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Image du produit */}
      <div className="relative">
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            className="w-full h-48 object-contain bg-gray-100"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-32 bg-gray-100 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}

        {/* Nutriscore */}
        {product.nutriscore && (
          <div className="absolute top-2 right-2">
            <NutriscoreBadge grade={product.nutriscore} />
          </div>
        )}
      </div>

      {/* Informations */}
      <div className="p-4">
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">{product.name}</h3>
          {product.brand && (
            <p className="text-gray-500 text-sm">{product.brand}</p>
          )}
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <span className="font-medium">Code-barres:</span>
            <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">{result.barcode}</span>
          </div>

          {product.category && (
            <div className="flex items-center gap-2 text-gray-600">
              <span className="font-medium">Catégorie:</span>
              <span>{product.category}</span>
            </div>
          )}

          {product.quantity && (
            <div className="flex items-center gap-2 text-gray-600">
              <span className="font-medium">Quantité:</span>
              <span>{product.quantity}</span>
            </div>
          )}

          {product.allergens && (
            <div className="mt-2">
              <span className="font-medium text-orange-600">Allergènes:</span>
              <p className="text-orange-600 text-xs mt-1">{product.allergens}</p>
            </div>
          )}
        </div>

        {/* Source */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs text-gray-400 flex items-center gap-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Données Open Food Facts
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="p-4 pt-0 flex gap-2">
        <Button onClick={() => onUseProduct(product)} className="flex-1">
          Utiliser ce produit
        </Button>
        <Button variant="outline" onClick={onScanAgain}>
          Rescanner
        </Button>
      </div>
    </div>
  );
}

interface NutriscoreBadgeProps {
  grade: string;
}

function NutriscoreBadge({ grade }: NutriscoreBadgeProps) {
  const colors: Record<string, string> = {
    A: 'bg-green-500',
    B: 'bg-lime-500',
    C: 'bg-yellow-500',
    D: 'bg-orange-500',
    E: 'bg-red-500',
  };

  const bgColor = colors[grade.toUpperCase()] || 'bg-gray-400';

  return (
    <div className={`${bgColor} text-white text-xs font-bold px-2 py-1 rounded shadow`}>
      Nutriscore {grade.toUpperCase()}
    </div>
  );
}

export { NutriscoreBadge };
